/**
 * CopaThemePlayerContext — estado ÚNICO do player do tema da Copa 2026.
 *
 * O handoff posiciona o player em DOIS lugares (cabeçalho da Convocação/onboarding
 * e cabeçalho do Álbum) que precisam compartilhar o MESMO estado: dar play num
 * reflete no outro, e há só UMA trilha tocando.
 *
 * Por isso o Provider dona um único elemento <audio loop> e expõe play/pause/volume
 * via contexto. Os componentes <ThemePlayer/> são só a casca visual.
 *
 * A trilha só toca por gesto explícito do usuário (botão play) — sem autoplay.
 *
 * Dependentes (o provider mora aqui, mas não é exclusivo da Copa — a trilha é o
 * tema institucional do Joga Junto BB): CopaModal e CopaOnboardingModal via
 * Extrato, e a página Manifesto. Cada um monta o próprio provider, então a
 * trilha para ao sair da página. Ao remover a campanha da Copa do código,
 * mover este arquivo em vez de apagá-lo — o Manifesto depende dele.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import themeSrc from 'assets/audio/copa/musica-copa.mp3';

const DEFAULT_VOLUME = 0.55;

interface CopaThemePlayerValue {
  /** URL da trilha (asset empacotado) — fonte única para tocar e baixar */
  src: string;
  /** true = tocando agora */
  playing: boolean;
  /** volume atual (0..1) */
  volume: number;
  /** true = áudio silenciado (mute), independente do volume */
  muted: boolean;
  /** alterna play/pause (gesto do usuário) */
  toggle: () => void;
  /** alterna mute on/off (gesto do usuário) */
  toggleMute: () => void;
  /** pausa a trilha e marca como pausa intencional (não retoma via gesto) */
  pause: () => void;
  /** define o volume (0..1); ajustar para >0 desfaz o mute */
  setVolume: (v: number) => void;
}

const CopaThemePlayerContext = createContext<CopaThemePlayerValue | null>(null);

export function CopaThemePlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolumeState] = useState(DEFAULT_VOLUME);
  const [muted, setMuted] = useState(false);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false)); // autoplay/gesto bloqueado
  }, []);

  // Sincroniza o volume do elemento sempre que mudar.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Sincroniza o mute do elemento sempre que mudar.
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  // Pausa ao desmontar o provider (sair do Dashboard).
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio?.pause();
    };
  }, []);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      play();
    } else {
      audio.pause();
      setPlaying(false);
    }
  }, [play]);

  // Pausa explícita (ex.: ao fechar a modal do álbum ou trocar de página).
  // Como não há botão de stop, rebobinamos a trilha para o início: assim, ao
  // reabrir, o usuário ouve o tema desde o começo em vez de retomar no meio.
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
  }, []);

  const toggleMute = useCallback(() => setMuted(m => !m), []);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.min(1, Math.max(0, v));
    setVolumeState(clamped);
    // Subir o volume manualmente desfaz o mute (padrão dos players).
    if (clamped > 0) setMuted(false);
  }, []);

  const value: CopaThemePlayerValue = {
    src: themeSrc,
    playing,
    volume,
    muted,
    toggle,
    toggleMute,
    pause,
    setVolume,
  };

  return (
    <CopaThemePlayerContext.Provider value={value}>
      <audio ref={audioRef} src={themeSrc} loop preload="auto" aria-hidden="true" />
      {children}
    </CopaThemePlayerContext.Provider>
  );
}

export function useCopaThemePlayer(): CopaThemePlayerValue {
  const ctx = useContext(CopaThemePlayerContext);
  if (!ctx) {
    throw new Error('useCopaThemePlayer deve ser usado dentro de <CopaThemePlayerProvider>.');
  }
  return ctx;
}
